import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronLeft, ChevronRight, Type, Sticker, Image as ImageIcon, CheckSquare, Bell, X, Copy, Palette, Eraser, LayoutGrid, Calendar as CalendarIcon, BookOpen, Clock } from 'lucide-react';
import AmethystDailyPlanner from './AmethystPlanner';
import AmethystWeeklyPlan from './AmethystWeeklyPlan';
import AmethystMonthlyPlanner from './AmethystMonthlyPlanner';
import MinSidaIndexView from './MinSidaIndexView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STICKERS = ['🌸', '✨', '🌟', '💗', '🐾', '🌿', '🍰', '☕', '🌈', '🦋', '🐣', '🐰', '🍀', '🌙', '📚', '💤', '🎀', '🍓', '🌷', '💌', '🧸', '🕯️', '🫧', '🐢'];
const COLORS   = ['#FFF5D1', '#FFC8DD', '#A2D2FF', '#B9FBC0', '#FDCFE8', '#C8B6FF', '#FFE8A3', '#E8F0FF'];

const todayYmd = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const humanDay = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
};

const shiftDay = (ymd, delta) => {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() + delta);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// ============== ALARM POPUP (mounted globally on this page) ==============
function AlarmPopup({ alarm, onAck }) {
  const audioRef = useRef(null);
  useEffect(() => {
    // Try to play a soft chime; browsers may block without user gesture
    if (audioRef.current && alarm.sound !== 'off') {
      audioRef.current.volume = 0.6;
      audioRef.current.play().catch(() => {});
    }
  }, [alarm.sound]);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" data-testid="mysida-alarm-popup">
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRlYAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YTIAAACAgIB/gYCBgIB/gYCBgIB/gYCBgH+BgIGAgH+BgIGAf4GAgYCAf4GAgYB/gYCBgIB/gYCBgA==" />
      <div
        className="neo-card p-6 max-w-md w-[92%] text-center"
        style={{ background: alarm.block_color || '#FFF5D1' }}
      >
        <div className="text-5xl mb-2 animate-bounce">🐶</div>
        <div className="text-xs uppercase tracking-widest font-bold text-gray-700">Farmor Corgi väcker dig</div>
        <div className="text-2xl font-['Fredoka'] font-bold mt-2 mb-1">⏰ {alarm.message}</div>
        {alarm.block_preview && alarm.block_preview !== alarm.message && (
          <div className="text-sm opacity-70 mt-1 italic">{alarm.block_preview}</div>
        )}
        <button
          onClick={() => onAck(alarm.alarm_id)}
          className="neo-btn mt-4 px-5 py-2 bg-white font-bold"
          data-testid="ack-alarm-btn"
        >
          Tack Farmor 💗
        </button>
      </div>
    </div>
  );
}

// ============== BLOCK ==============
function BlockCard({ block, onUpdate, onDelete, onOpenAlarms }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(block.content || '');
  useEffect(() => { setContent(block.content || ''); }, [block.content]);

  const commit = () => {
    setEditing(false);
    if (content !== block.content) {
      onUpdate({ content });
    }
  };

  const alarmCount = (block.alarms || []).length;

  return (
    <div
      className="neo-card relative group flex flex-col"
      style={{
        background: block.color,
        transform: `rotate(${block.rotation || 0}deg)`,
        minHeight: 100,
      }}
      data-testid={`mysida-block-${block.id}`}
    >
      {/* Toolbar */}
      <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        <button
          onClick={() => onOpenAlarms(block)}
          className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border-2 border-black flex items-center gap-0.5 ${
            alarmCount > 0 ? 'bg-black text-white' : 'bg-white'
          }`}
          title="Alarm"
          data-testid={`block-alarms-btn-${block.id}`}
        >
          <Bell className="w-3 h-3" />
          {alarmCount > 0 && <span>{alarmCount}</span>}
        </button>
        <button
          onClick={() => {
            if (window.confirm('Ta bort blocket?')) onDelete();
          }}
          className="px-1 py-0.5 rounded-full border-2 border-black bg-white"
          title="Ta bort"
          data-testid={`block-delete-btn-${block.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {alarmCount > 0 && (
        <div className="absolute -top-2 -left-2 bg-black text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
          <Bell className="w-2.5 h-2.5" /> {alarmCount}
        </div>
      )}

      {/* Content */}
      {block.type === 'sticker' && (
        <div className="flex-1 flex items-center justify-center text-6xl select-none p-2">
          {block.sticker || '✨'}
        </div>
      )}
      {block.type === 'image' && block.image_base64 && (
        <img
          src={`data:image/jpeg;base64,${block.image_base64}`}
          alt=""
          className="w-full h-full object-cover rounded-md"
          style={{ maxHeight: 220 }}
        />
      )}
      {block.type === 'doodle' && block.image_base64 && (
        <div className="p-2 flex-1 flex items-center justify-center" style={{ background: 'repeating-linear-gradient(45deg,#fff,#fff 6px,#fafafa 6px,#fafafa 12px)' }}>
          <img
            src={`data:image/png;base64,${block.image_base64}`}
            alt=""
            className="max-w-full rounded-md"
            style={{ maxHeight: 220 }}
          />
        </div>
      )}
      {(block.type === 'text' || block.type === 'checkbox') && (
        <div className="p-3 flex-1">
          {block.type === 'checkbox' && (
            <button
              onClick={() => onUpdate({ checked: !block.checked })}
              className={`inline-flex items-center justify-center w-5 h-5 mr-2 rounded border-2 border-black align-middle ${
                block.checked ? 'bg-black' : 'bg-white'
              }`}
              data-testid={`block-check-${block.id}`}
            >
              {block.checked && <CheckSquare className="w-3 h-3 text-white" />}
            </button>
          )}
          {editing ? (
            <textarea
              autoFocus
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onBlur={commit}
              className="bg-transparent w-full outline-none resize-none font-['Fredoka'] text-sm min-h-[60px]"
              data-testid={`block-textarea-${block.id}`}
            />
          ) : (() => {
            const m = /^\[\[([^\]]{1,30})\]\]\n?/.exec(content);
            const title = m ? m[1] : null;
            const body = m ? content.slice(m[0].length) : content;
            return (
              <div
                onClick={() => setEditing(true)}
                className="text-sm font-['Fredoka'] leading-snug whitespace-pre-wrap break-words cursor-text min-h-[40px]"
                data-testid={`block-text-${block.id}`}
              >
                {title && (
                  <div className="uppercase tracking-widest text-[10px] font-black text-center py-1 -mx-3 -mt-3 mb-2 bg-black/10 rounded-t-md">
                    {title}
                  </div>
                )}
                {body || <span className="opacity-40 italic">Klicka för att skriva…</span>}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ============== ALARM MODAL ==============
function AlarmModal({ block, day, onClose, onSaved }) {
  const initial = (block.alarms || []).map((a) => ({
    at_local: a.at ? new Date(a.at).toISOString().slice(0, 16) : '',
    message: a.message || '',
    sound: a.sound || 'corgi',
  }));
  while (initial.length < 3) {
    initial.push({ at_local: '', message: '', sound: 'corgi' });
  }
  const [rows, setRows] = useState(initial.slice(0, 3));
  const [saving, setSaving] = useState(false);

  const update = (i, key, value) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: value };
    setRows(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const alarms = rows
        .filter((r) => r.at_local)
        .map((r) => ({
          at: new Date(r.at_local).toISOString(),
          message: r.message,
          sound: r.sound,
        }));
      await axios.put(`${API}/mysida/page/${day}/block/${block.id}`, { alarms }, { withCredentials: true });
      toast.success(alarms.length ? `${alarms.length} alarm sparade ⏰` : 'Alarm rensade');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Kunde inte spara alarm');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="alarm-modal">
      <div className="neo-card bg-white p-5 max-w-md w-full">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-['Fredoka'] font-bold flex items-center gap-2">
            <Bell className="w-5 h-5" /> Alarm (max 3)
          </div>
          <button onClick={onClose} className="neo-btn p-1 bg-white" data-testid="alarm-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs opacity-70 mb-3 italic truncate">
          "{(block.content || block.sticker || 'blocket').slice(0, 50)}"
        </div>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="p-2 border-2 border-black rounded-xl bg-[#FFF5D1]/40" data-testid={`alarm-row-${i}`}>
              <label className="text-[10px] uppercase tracking-widest font-bold text-gray-700">Alarm {i + 1}</label>
              <input
                type="datetime-local"
                value={r.at_local}
                onChange={(e) => update(i, 'at_local', e.target.value)}
                className="w-full mt-1 px-2 py-1 rounded-lg border-2 border-black bg-white text-sm"
                data-testid={`alarm-time-${i}`}
              />
              <input
                type="text"
                placeholder="Valfri egen text (annars blockets innehåll)"
                value={r.message}
                onChange={(e) => update(i, 'message', e.target.value)}
                className="w-full mt-2 px-2 py-1 rounded-lg border-2 border-black bg-white text-sm"
                data-testid={`alarm-msg-${i}`}
              />
              <select
                value={r.sound}
                onChange={(e) => update(i, 'sound', e.target.value)}
                className="w-full mt-2 px-2 py-1 rounded-lg border-2 border-black bg-white text-sm"
                data-testid={`alarm-sound-${i}`}
              >
                <option value="corgi">🐶 Farmor Corgi</option>
                <option value="bell">🔔 Klocka</option>
                <option value="chime">🎐 Klingande</option>
                <option value="off">🤫 Tyst</option>
              </select>
              {r.at_local && (
                <button
                  onClick={() => { update(i, 'at_local', ''); update(i, 'message', ''); }}
                  className="text-xs mt-1 text-red-600 underline"
                  data-testid={`alarm-clear-${i}`}
                >
                  Ta bort alarm
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="neo-btn mt-4 px-4 py-2 bg-[#B9FBC0] font-bold w-full"
          data-testid="alarm-save-btn"
        >
          {saving ? 'Sparar…' : 'Spara alarm 💾'}
        </button>
      </div>
    </div>
  );
}

// ============== DOODLE PAD ==============
const DOODLE_COLORS = ['#111111', '#FF6EB4', '#7AB8FF', '#FFD166', '#8FE39F', '#C8B6FF'];

function DoodlePad({ onSave, onClose }) {
  const canvasRef = useRef(null);
  const [color, setColor] = useState('#111111');
  const [size, setSize] = useState(4);
  const [eraser, setEraser] = useState(false);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const x = (e.clientX - r.left) * (c.width / r.width);
    const y = (e.clientY - r.top) * (c.height / r.height);
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.strokeStyle = eraser ? '#ffffff' : color;
    ctx.lineWidth = eraser ? size * 3 : size;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const save = () => {
    const c = canvasRef.current;
    const dataUrl = c.toDataURL('image/png');
    const b64 = dataUrl.split(',')[1];
    onSave(b64);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="doodle-modal">
      <div
        className="neo-card bg-white p-4 w-full max-w-lg space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="text-lg font-black flex items-center gap-2"><Palette className="w-5 h-5" /> Rita något gulligt</div>
          <button onClick={onClose} className="text-black/60" data-testid="doodle-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          {DOODLE_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { setColor(c); setEraser(false); }}
              className={`w-7 h-7 rounded-full border-2 transition ${!eraser && color === c ? 'border-black scale-110' : 'border-black/20'}`}
              style={{ background: c }}
              data-testid={`doodle-color-${c.replace('#','')}`}
              aria-label={`Färg ${c}`}
            />
          ))}
          <button
            onClick={() => setEraser((v) => !v)}
            className={`neo-btn px-2 py-1 flex items-center gap-1 text-xs font-bold ${eraser ? 'bg-[#FFD166]' : 'bg-white'}`}
            data-testid="doodle-eraser-btn"
          >
            <Eraser className="w-3.5 h-3.5" /> Sudda
          </button>
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-xs text-black/60">Tjocklek</span>
            <input type="range" min="2" max="18" value={size} onChange={(e) => setSize(Number(e.target.value))} className="w-24" data-testid="doodle-size" />
          </div>
        </div>

        <canvas
          ref={canvasRef}
          width={520}
          height={340}
          className="w-full rounded-md border-2 border-black bg-white touch-none cursor-crosshair"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          data-testid="doodle-canvas"
        />

        <div className="flex justify-between items-center gap-2">
          <button onClick={clear} className="neo-btn px-3 py-2 bg-white text-sm font-bold" data-testid="doodle-clear-btn">
            <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Rensa
          </button>
          <button onClick={save} className="neo-btn px-4 py-2 bg-[#B9FBC0] text-sm font-black" data-testid="doodle-save-btn">
            Spara teckning 🎨
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== WEEK STRIP ==============
function WeekStrip({ day, onPick, refreshKey }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/mysida/week?day=${day}`, { withCredentials: true })
      .then((r) => { if (!cancel) setDays(r.data.days || []); })
      .catch(() => { if (!cancel) setDays([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [day, refreshKey]);

  if (loading) return null;
  return (
    <div className="mb-4 grid grid-cols-7 gap-1.5" data-testid="mysida-week-strip">
      {days.map((d) => {
        const active = d.day === day;
        const empty = d.block_count === 0;
        return (
          <button
            key={d.day}
            onClick={() => onPick(d.day)}
            className={`relative neo-btn p-1.5 flex flex-col items-center justify-start gap-0.5 min-h-[72px] text-[10px] transition ${
              active ? 'bg-[#C8B6FF] scale-[1.03] ring-2 ring-[#7D6BA0]' :
              d.is_today ? 'bg-[#E8DCF5]' : 'bg-white'
            }`}
            data-testid={`week-cell-${d.day}`}
            title={d.day}
          >
            <div className="font-bold uppercase leading-none text-[#7D6BA0]">
              {d.weekday_label}
              {d.is_today && <span className="text-[8px] ml-0.5 opacity-70">•</span>}
            </div>
            <div className="text-[11px] font-black leading-none text-[#4E4370]">{d.day.slice(-2)}</div>

            {empty ? (
              <div className="text-[9px] opacity-40 mt-0.5">tom</div>
            ) : (
              <>
                <div className="text-[15px] leading-none">
                  {d.stickers.length > 0
                    ? d.stickers.slice(0, 3).join('')
                    : d.has_doodle ? '🎨'
                    : d.has_image ? '📸'
                    : '📝'}
                </div>
                {d.text_preview && (
                  <div className="text-[8px] opacity-70 line-clamp-1 px-0.5 text-center">
                    {d.text_preview}
                  </div>
                )}
              </>
            )}

            {d.alarm_count > 0 && (
              <div className="absolute -top-1 -right-1 bg-black text-white text-[8px] font-bold rounded-full px-1 py-0.5 flex items-center gap-0.5">
                <Bell className="w-2 h-2" /> {d.alarm_count}
              </div>
            )}
            {d.block_count > 0 && (
              <div className="absolute -bottom-1 -left-1 bg-white border-2 border-black text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {d.block_count}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ============== ADD BLOCK PANEL ==============
// ============== TEMPLATE LIBRARY ==============
const LIB_LILA_LIGHT = '#E8DCF5';
const LIB_LILA_MID   = '#C8B6FF';
const LIB_LILA_DEEP  = '#9A87C5';
const LIB_PINK       = '#FFD5F0';

const LIBRARY_CATEGORIES = [
  { id: 'favorites', label: 'Favoriter', emoji: '⭐' },
  { id: 'templates', label: 'Mallar', emoji: '📋' },
  { id: 'cards', label: 'Kort', emoji: '🗂️' },
  { id: 'washi', label: 'Washi-tejp', emoji: '🎀' },
  { id: 'weather', label: 'Väder', emoji: '☀️' },
  { id: 'icons', label: 'Ikoner', emoji: '💫' },
  { id: 'shapes', label: 'Former', emoji: '⭕' },
];

// Full templates that insert MANY blocks at once (Amethyst-planner-style)
const DAILY_PLANNER_TEMPLATE = [
  { title: 'TOP PRIORITIES', body: '1. \n2. \n3. ',                          color: '#DED0F0' },
  { title: 'TO-DO LIST',     body: '• \n• \n• \n• \n• \n• \n• ',              color: LIB_LILA_LIGHT },
  { title: 'MOOD',           body: '😖   😕   🙂   😊   🤩',                   color: '#F5EEFF' },
  { title: 'WATER BALANCE',  body: '□  □  □  □  □  □  □  □',                  color: '#E8F0FF' },
  { title: 'MEALS',          body: 'B: \nL: \nD: \nSnack: ',                  color: '#FFE8F5' },
  { title: 'NOTES',          body: '',                                        color: LIB_LILA_MID },
  { title: 'THANKS FOR',     body: '1. \n2. \n3. \n4. \n5. ',                 color: '#FFD5F0' },
  { title: 'PRODUCTIVITY',   body: '☆ ☆ ☆ ☆ ☆',                              color: '#FFF5D1' },
  { title: 'ROUTINES',       body: '☐ Morgon \n☐ Middag \n☐ Kväll ',          color: '#DED0F0' },
  { title: 'SCHEDULE 06–14', body: '06:00 \n07:00 \n08:00 \n09:00 \n10:00 \n11:00 \n12:00 \n13:00 \n14:00', color: LIB_LILA_LIGHT },
  { title: 'SCHEDULE 15–23', body: '15:00 \n16:00 \n17:00 \n18:00 \n19:00 \n20:00 \n21:00 \n22:00 \n23:00', color: LIB_LILA_LIGHT },
];

const WEEKLY_PLANNER_TEMPLATE = [
  { title: 'MÅN', body: '', color: LIB_LILA_LIGHT }, { title: 'TIS', body: '', color: LIB_LILA_LIGHT },
  { title: 'ONS', body: '', color: LIB_LILA_LIGHT }, { title: 'TOR', body: '', color: LIB_LILA_LIGHT },
  { title: 'FRE', body: '', color: LIB_LILA_LIGHT }, { title: 'LÖR', body: '', color: LIB_LILA_LIGHT },
  { title: 'SÖN', body: '', color: LIB_LILA_LIGHT }, { title: 'NOTES', body: '', color: LIB_PINK },
];

const LIBRARY_TEMPLATES = [
  { id: 'daily', label: 'Daily Planner', emoji: '📓', desc: '11 kort — Priorities, To-Do, Mood, Water, Meals, Notes, Thanks, Productivity, Routines, Schedule', items: DAILY_PLANNER_TEMPLATE },
  { id: 'weekly', label: 'Weekly Plan', emoji: '📅', desc: '7 dagsboxar + NOTES', items: WEEKLY_PLANNER_TEMPLATE },
];

const LIBRARY_ITEMS = {
  cards: [
    { title: 'THIS WEEK', body: 'M \nT \nO \nT \nF \nL \nS ', color: LIB_LILA_MID },
    { title: 'NOTES',     body: '',                            color: LIB_LILA_LIGHT },
    { title: 'MEMORIES',  body: '',                            color: LIB_PINK },
    { title: 'READING',   body: '',                            color: LIB_LILA_MID },
    { title: 'WATCHING',  body: '',                            color: LIB_LILA_LIGHT },
    { title: 'LISTENING TO', body: '',                         color: LIB_PINK },
    { title: 'GRATEFUL FOR', body: '',                         color: '#FFF5D1' },
    { title: 'DONT FORGET', body: '',                          color: '#FFCFCF' },
  ],
  washi: [
    { sticker: '▬▬▬▬▬', color: LIB_LILA_MID, rotation: -1.5 },
    { sticker: '▬▬▬▬▬', color: LIB_PINK,     rotation: 2 },
    { sticker: '▬▬▬▬▬', color: LIB_LILA_DEEP, rotation: -0.5 },
    { sticker: '▬▬▬▬▬', color: '#B5A0D0',    rotation: 1 },
    { sticker: '▬▬▬▬▬', color: '#FFC8DD',    rotation: -2 },
    { sticker: '▬▬▬▬▬', color: '#FDF0D5',    rotation: 0.5 },
  ],
  weather: [
    { sticker: '☀️', color: '#FFF5D1' }, { sticker: '⛅', color: '#E8F0FF' },
    { sticker: '☁️', color: '#F3F4F6' }, { sticker: '🌧️', color: '#DBEAFE' },
    { sticker: '⛈️', color: '#C7D2FE' }, { sticker: '🌨️', color: '#F0F9FF' },
    { sticker: '❄️', color: '#F0F9FF' }, { sticker: '💨', color: '#F3F4F6' },
    { sticker: '🌈', color: '#FFE8F5' }, { sticker: '🌙', color: '#E5D9F5' },
    { sticker: '⭐', color: '#FFF5D1' }, { sticker: '✨', color: '#F5EEFF' },
  ],
  icons: [
    { sticker: '💗', color: '#FFC8DD' }, { sticker: '⭐', color: '#FFF5D1' },
    { sticker: '💧', color: '#A2D2FF' }, { sticker: '🍴', color: '#E8DCF5' },
    { sticker: '🛒', color: '#FFD5F0' }, { sticker: '🐾', color: '#C8B6FF' },
    { sticker: '💰', color: '#B9FBC0' }, { sticker: '⏰', color: '#FDF0D5' },
    { sticker: '💊', color: '#FFCFCF' }, { sticker: '🦷', color: '#F5F5F5' },
    { sticker: '💻', color: '#DBEAFE' }, { sticker: '📺', color: '#E5D9F5' },
    { sticker: '🎮', color: '#FDF0D5' }, { sticker: '👟', color: '#FFC8DD' },
    { sticker: '✈️', color: '#A2D2FF' }, { sticker: '🎂', color: '#FFE8F5' },
    { sticker: '🏠', color: '#B9FBC0' }, { sticker: '📚', color: '#E8DCF5' },
    { sticker: '☕', color: '#FDF0D5' }, { sticker: '🧘', color: '#F5EEFF' },
  ],
  shapes: [
    { sticker: '●', color: LIB_LILA_DEEP }, { sticker: '◐', color: LIB_LILA_MID },
    { sticker: '◑', color: LIB_LILA_MID },  { sticker: '○', color: LIB_LILA_LIGHT },
    { sticker: '★', color: '#FFF5D1' },     { sticker: '✓', color: '#B9FBC0' },
    { sticker: '✗', color: '#FFCFCF' },     { sticker: '→', color: LIB_LILA_MID },
    { sticker: '❤', color: '#FFC8DD' },     { sticker: '!', color: '#FFD5F0' },
  ],
};

function TemplateLibrary({ onPick, onPickBulk, onClose }) {
  const [cat, setCat] = useState('favorites');
  const [favs, setFavs] = useState([]);
  const items = LIBRARY_ITEMS[cat] || [];

  useEffect(() => {
    axios.get(`${API}/mysida/library-favorites`, { withCredentials: true })
      .then((r) => setFavs(r.data.favorites || []))
      .catch(() => setFavs([]));
  }, []);

  const isFav = (key) => favs.some((f) => f.key === key);

  const toggleFav = async (key, label, kind, payload) => {
    try {
      await axios.post(`${API}/mysida/library-favorites/toggle`, { key, label, kind, payload }, { withCredentials: true });
      const r = await axios.get(`${API}/mysida/library-favorites`, { withCredentials: true });
      setFavs(r.data.favorites || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara favorit');
    }
  };

  const itemKey = (catId, i) => `${catId}:${i}`;

  const buildPayload = (catId, item) => {
    if (catId === 'cards') return { type: 'text', content: `[[${item.title}]]\n${item.body || ''}`, color: item.color, rotation: (Math.random() * 4 - 2) };
    return { type: 'sticker', sticker: item.sticker, color: item.color, rotation: item.rotation != null ? item.rotation : (Math.random() * 6 - 3) };
  };

  const insertTemplate = (tpl) => {
    const payloads = tpl.items.map((it) => ({
      type: 'text',
      content: `[[${it.title}]]\n${it.body || ''}`,
      color: it.color,
      rotation: (Math.random() * 3 - 1.5),
    }));
    onPickBulk(payloads);
  };

  const insert = (item) => {
    // Cards → text block with [[TITLE]] header
    if ('title' in item) {
      onPick({
        type: 'text',
        content: `[[${item.title}]]\n${item.body || ''}`,
        color: item.color,
        rotation: (Math.random() * 4 - 2),
      });
    } else {
      // Washi / weather / icons / shapes → sticker block
      onPick({
        type: 'sticker',
        sticker: item.sticker,
        color: item.color,
        rotation: item.rotation != null ? item.rotation : (Math.random() * 6 - 3),
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="library-modal">
      <div className="neo-card bg-white p-4 w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-black flex items-center gap-2">📚 Klistermärkes-bibliotek</div>
          <button onClick={onClose} className="text-black/60" data-testid="library-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 mb-3 flex-wrap">
          {LIBRARY_CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className={`neo-btn px-2.5 py-1.5 text-xs font-bold ${cat === c.id ? 'bg-[#C8B6FF]' : 'bg-white'}`}
              data-testid={`library-cat-${c.id}`}
            >
              <span className="mr-1">{c.emoji}</span>
              {c.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="overflow-auto flex-1 pr-1">
          {cat === 'favorites' && (
            favs.length === 0 ? (
              <div className="text-center py-12 opacity-60 text-sm" data-testid="library-grid">
                <div className="text-4xl mb-2">⭐</div>
                <div>Inga favoriter än — tryck på ⭐ på ett klistermärke i annan flik</div>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="library-grid">
                {favs.map((f) => (
                  <div key={f.key} className="relative">
                    <button
                      onClick={() => { onPick(f.payload); onClose(); }}
                      className="w-full neo-card p-2 hover:scale-105 transition text-left overflow-hidden"
                      style={{ background: f.payload?.color || '#E8DCF5', minHeight: 64 }}
                      data-testid={`library-fav-${f.key}`}
                    >
                      <div className="text-center text-2xl leading-none">
                        {f.payload?.sticker || (f.kind === 'card' ? '🗂️' : '🎀')}
                      </div>
                      <div className="text-[9px] text-center mt-1 opacity-70 line-clamp-1">{f.label}</div>
                    </button>
                    <button
                      onClick={() => toggleFav(f.key, f.label, f.kind, f.payload)}
                      className="absolute -top-1 -right-1 bg-white border-2 border-black rounded-full w-5 h-5 flex items-center justify-center text-[10px]"
                      title="Ta bort favorit"
                      data-testid={`library-fav-remove-${f.key}`}
                    >
                      ⭐
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
          {cat === 'templates' && (
            <div className="space-y-2" data-testid="library-grid">
              {LIBRARY_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => { insertTemplate(tpl); onClose(); }}
                  className="w-full neo-card p-3 text-left hover:scale-[1.01] transition bg-gradient-to-br from-[#E8DCF5] to-[#C8B6FF]"
                  data-testid={`library-template-${tpl.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{tpl.emoji}</span>
                    <span className="font-black text-lg text-[#4E4370]">{tpl.label}</span>
                    <span className="ml-auto text-[10px] bg-white px-2 py-0.5 rounded-full text-[#7D6BA0]">{tpl.items.length} kort</span>
                  </div>
                  <div className="text-[11px] text-[#4E4370]/80">{tpl.desc}</div>
                </button>
              ))}
            </div>
          )}
          {cat === 'cards' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="library-grid">
              {items.map((it, i) => {
                const key = itemKey('cards', i);
                return (
                  <div key={i} className="relative">
                    <button
                      onClick={() => { insert(it); onClose(); }}
                      className="w-full neo-card p-0 hover:scale-105 transition text-left overflow-hidden"
                      style={{ background: it.color }}
                      data-testid={`library-item-${i}`}
                    >
                      <div className="bg-black/10 uppercase tracking-widest text-[9px] font-black py-1 text-center">{it.title}</div>
                      <div className="p-2 h-16 text-[10px] whitespace-pre-line opacity-60">{it.body || '—'}</div>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFav(key, it.title, 'card', buildPayload('cards', it)); }}
                      className={`absolute top-1 right-1 rounded-full w-6 h-6 flex items-center justify-center text-[11px] transition ${isFav(key) ? 'bg-[#FFD166]' : 'bg-white/80 hover:bg-white'}`}
                      title={isFav(key) ? 'Ta bort favorit' : 'Spara som favorit'}
                      data-testid={`library-fav-toggle-${key}`}
                    >
                      {isFav(key) ? '⭐' : '☆'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {(cat === 'washi') && (
            <div className="space-y-2" data-testid="library-grid">
              {items.map((it, i) => {
                const key = itemKey('washi', i);
                return (
                  <div key={i} className="relative">
                    <button
                      onClick={() => { insert(it); onClose(); }}
                      className="w-full neo-card py-3 hover:scale-[1.02] transition flex items-center justify-center text-white font-black text-xs tracking-widest"
                      style={{ background: it.color, transform: `rotate(${it.rotation}deg)` }}
                      data-testid={`library-item-${i}`}
                    >
                      WASHI TAPE
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFav(key, 'Washi', 'washi', buildPayload('washi', it)); }}
                      className={`absolute top-1 right-2 rounded-full w-6 h-6 flex items-center justify-center text-[11px] ${isFav(key) ? 'bg-[#FFD166]' : 'bg-white/80'}`}
                      data-testid={`library-fav-toggle-${key}`}
                    >
                      {isFav(key) ? '⭐' : '☆'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {(cat === 'weather' || cat === 'icons' || cat === 'shapes') && (
            <div className="grid grid-cols-5 sm:grid-cols-6 gap-2" data-testid="library-grid">
              {items.map((it, i) => {
                const key = itemKey(cat, i);
                return (
                  <div key={i} className="relative">
                    <button
                      onClick={() => { insert(it); onClose(); }}
                      className="w-full neo-card p-2 aspect-square flex items-center justify-center text-3xl hover:scale-110 transition"
                      style={{ background: it.color }}
                      data-testid={`library-item-${i}`}
                    >
                      {it.sticker}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFav(key, it.sticker, 'sticker', buildPayload(cat, it)); }}
                      className={`absolute -top-1 -right-1 rounded-full w-5 h-5 flex items-center justify-center text-[10px] border border-black/20 ${isFav(key) ? 'bg-[#FFD166]' : 'bg-white/80'}`}
                      data-testid={`library-fav-toggle-${key}`}
                    >
                      {isFav(key) ? '⭐' : '☆'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-3 text-[10px] text-black/50 text-center">
          Klicka på ett klistermärke — det landar på sidan och du kan redigera/flytta/lägga alarm 💗
        </div>
      </div>
    </div>
  );
}

function AddBlockPanel({ onAdd }) {
  const fileRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(null); // 'sticker' | 'color' | null
  const [doodleOpen, setDoodleOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const pickColor = () => COLORS[Math.floor(Math.random() * COLORS.length)];
  const smallRot = () => (Math.random() * 6 - 3);

  const addText = () => onAdd({ type: 'text', content: '', color: pickColor(), rotation: smallRot() });
  const addCheckbox = () => onAdd({ type: 'checkbox', content: '', color: pickColor(), rotation: smallRot(), checked: false });
  const addSticker = (s) => { onAdd({ type: 'sticker', sticker: s, color: pickColor(), rotation: smallRot() }); setPickerOpen(null); };
  const addDoodle = (b64) => { onAdd({ type: 'doodle', image_base64: b64, color: '#ffffff', rotation: smallRot() }); setDoodleOpen(false); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Bilden är för stor (max 5MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(',')[1];
      onAdd({ type: 'image', image_base64: b64, color: '#ffffff', rotation: smallRot() });
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  return (
    <div className="mb-4 flex flex-wrap gap-2 items-center" data-testid="mysida-toolbar">
      <button onClick={addText} className="neo-btn px-3 py-2 bg-[#FFF5D1] flex items-center gap-1.5 text-sm font-bold" data-testid="add-text-btn">
        <Type className="w-4 h-4" /> Text
      </button>
      <button onClick={addCheckbox} className="neo-btn px-3 py-2 bg-[#B9FBC0] flex items-center gap-1.5 text-sm font-bold" data-testid="add-checkbox-btn">
        <CheckSquare className="w-4 h-4" /> Bock-lista
      </button>
      <button
        onClick={() => setPickerOpen(pickerOpen === 'sticker' ? null : 'sticker')}
        className="neo-btn px-3 py-2 bg-[#FFC8DD] flex items-center gap-1.5 text-sm font-bold"
        data-testid="add-sticker-btn"
      >
        <Sticker className="w-4 h-4" /> Klistermärke
      </button>
      <button onClick={() => fileRef.current?.click()} className="neo-btn px-3 py-2 bg-[#A2D2FF] flex items-center gap-1.5 text-sm font-bold" data-testid="add-image-btn">
        <ImageIcon className="w-4 h-4" /> Bild
      </button>
      <button
        onClick={() => setDoodleOpen(true)}
        className="neo-btn px-3 py-2 bg-[#C8B6FF] flex items-center gap-1.5 text-sm font-bold"
        data-testid="add-doodle-btn"
      >
        <Palette className="w-4 h-4" /> Rita
      </button>
      <button
        onClick={() => setLibOpen(true)}
        className="neo-btn px-3 py-2 bg-[#E8DCF5] flex items-center gap-1.5 text-sm font-bold"
        data-testid="open-library-btn"
      >
        📚 Bibliotek
      </button>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="mysida-image-input" />

      {pickerOpen === 'sticker' && (
        <div className="basis-full neo-card bg-white p-2 flex flex-wrap gap-1" data-testid="sticker-picker">
          {STICKERS.map((s) => (
            <button key={s} onClick={() => addSticker(s)} className="text-2xl hover:scale-125 transition" data-testid={`sticker-${s}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      {doodleOpen && (
        <DoodlePad onSave={addDoodle} onClose={() => setDoodleOpen(false)} />
      )}

      {libOpen && (
        <TemplateLibrary
          onPick={(payload) => onAdd(payload)}
          onPickBulk={(payloads) => { payloads.forEach((p) => onAdd(p)); }}
          onClose={() => setLibOpen(false)}
        />
      )}
    </div>
  );
}

// ============== YEAR GRID VIEW ==============
function YearView({ onPickMonth, onPickDay }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/mysida/year-grid?year=${year}`, { withCredentials: true })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [year]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="mysida-year">
      <div className="neo-card bg-gradient-to-br from-[#F5EEFF] via-[#E8F0FF] to-[#FFE8F5] p-5 text-center mb-4">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-700 mb-1">Årsöversikt</div>
        <div className="font-['Fredoka'] text-3xl sm:text-4xl font-bold">{year}</div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <button onClick={() => setYear(year - 1)} className="neo-btn p-1.5 bg-white" data-testid="year-prev-btn"><ChevronLeft className="w-3.5 h-3.5" /></button>
          <button onClick={() => setYear(new Date().getFullYear())} className="text-xs underline" data-testid="year-jump-today-btn">→ i år</button>
          <button onClick={() => setYear(year + 1)} className="neo-btn p-1.5 bg-white" data-testid="year-next-btn"><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-center opacity-60 py-10">Laddar året… 🌸</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {data.months.map((m) => (
            <button
              key={m.month}
              onClick={() => onPickMonth(year, m.month)}
              className={`neo-card p-2 text-left transition hover:scale-[1.02] ${
                data.current_month === m.month ? 'bg-[#FFF5D1]' : 'bg-white'
              }`}
              data-testid={`year-month-${m.month}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-sm font-['Fredoka'] font-bold capitalize">{m.label}</div>
                <div className="text-[9px] uppercase text-gray-500">{m.month.toString().padStart(2, '0')}</div>
              </div>
              {/* Weekday header */}
              <div className="grid grid-cols-7 gap-[1px] mb-0.5 text-center text-[7px] font-bold text-gray-400 uppercase">
                {WEEKDAY_MINI.map((w, i) => <div key={i}>{w}</div>)}
              </div>
              {/* Weeks */}
              <div className="space-y-[1px]">
                {m.weeks.map((wk, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-[1px]">
                    {wk.map((c) => (
                      <div
                        key={c.day}
                        onClick={(e) => { e.stopPropagation(); if (c.in_month) onPickDay(c.day); }}
                        className={`relative aspect-square flex items-center justify-center text-[8px] font-bold rounded cursor-pointer transition ${
                          !c.in_month ? 'opacity-20 text-gray-400' :
                          c.is_today ? 'bg-black text-white' :
                          c.has_content ? 'bg-[#FFC8DD] hover:scale-110' :
                          'bg-gray-50 hover:bg-[#E8F0FF]'
                        }`}
                        title={c.in_month ? c.day : ''}
                      >
                        {parseInt(c.day.slice(-2), 10)}
                        {c.alarm_count > 0 && c.in_month && (
                          <span className="absolute top-0 right-0 w-1 h-1 bg-red-500 rounded-full" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 text-[10px] text-black/50 flex gap-3 flex-wrap justify-center">
        <span>⬛ idag</span><span>🌸 = innehåll</span><span>🔴 = alarm</span>
      </div>
    </div>
  );
}

// ============== MONTH VIEW ==============
const MONTH_LABELS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const WEEKDAY_MINI = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];

function MonthView({ initialDay, onPickDay }) {
  const initial = new Date(`${initialDay}T12:00:00`);
  const [year, setYear]   = useState(initial.getFullYear());
  const [month, setMonth] = useState(initial.getMonth() + 1);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/mysida/month?year=${year}&month=${month}`, { withCredentials: true })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [year, month]);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const jumpToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  };

  return (
    <div className="neo-card p-4 bg-white md:pr-12 relative" data-testid="mysida-month">
      {/* Header row like Amethyst */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-2xl sm:text-3xl font-black tracking-[0.15em] text-[#7D6BA0]">MONTHLY&nbsp;&nbsp;PLAN</div>
          <div className="text-[11px] uppercase tracking-widest text-[#8B7BAA] mt-0.5">
            {data?.month_label || MONTH_LABELS[month - 1]} {year}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="neo-btn p-2 bg-white" data-testid="month-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={jumpToday} className="neo-btn px-2 py-1 bg-[#E8DCF5] text-xs font-bold" data-testid="month-jump-today-btn">
            i månad
          </button>
          <button onClick={() => shift(1)} className="neo-btn p-2 bg-white" data-testid="month-next-btn">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Weekday header — Amethyst-styled, Swedish week (Mon-first) */}
      <div className="grid grid-cols-7 mb-0.5 text-center text-[10px] tracking-widest font-bold text-[#8B7BAA] uppercase bg-[#E8DCF5] rounded-t-md">
        {['MÅN','TIS','ONS','TOR','FRE','LÖR','SÖN'].map((w) => (
          <div key={w} className="py-1.5">{w}</div>
        ))}
      </div>

      {loading ? (
        <div className="text-center opacity-60 py-10">Laddar månad… 🌸</div>
      ) : !data ? (
        <div className="text-center opacity-60 py-10">Kunde inte ladda</div>
      ) : (
        <div>
          {data.weeks.map((wk, wi) => (
            <div key={wi} className="grid grid-cols-7 border-l border-t border-[#DED0F0]">
              {wk.map((c) => (
                <button
                  key={c.day}
                  onClick={() => onPickDay(c.day)}
                  className={`relative min-h-[74px] p-1.5 flex flex-col text-left text-[10px] border-r border-b border-[#DED0F0] transition ${
                    !c.in_month ? 'bg-white opacity-40' :
                    c.is_today ? 'bg-[#DED0F0]' :
                    c.block_count > 0 ? 'bg-white hover:bg-[#F5EEFF]' :
                    'bg-white hover:bg-[#F5EEFF]'
                  }`}
                  data-testid={`month-cell-${c.day}`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`font-bold leading-none ${c.is_today ? 'text-[#4E4370]' : 'text-[#8B7BAA]'}`}>
                      {parseInt(c.day.slice(-2), 10)}
                      {c.is_today && <span className="ml-1 w-1 h-1 inline-block bg-[#7D6BA0] rounded-full align-middle" />}
                    </div>
                    {c.alarm_count > 0 && (
                      <div className="text-[8px] bg-[#7D6BA0] text-white rounded-full px-1 flex items-center gap-0.5">
                        <Bell className="w-2 h-2" /> {c.alarm_count}
                      </div>
                    )}
                  </div>
                  <div className="mt-0.5 h-px bg-[#DED0F0] w-8" />
                  {c.stickers.length > 0 && (
                    <div className="text-[13px] leading-none mt-1">{c.stickers.join('')}</div>
                  )}
                  {(c.has_doodle || c.has_image) && c.stickers.length === 0 && (
                    <div className="text-[12px] leading-none mt-1 text-[#8B7BAA]">
                      {c.has_doodle ? '🎨' : ''}{c.has_image ? '📸' : ''}
                    </div>
                  )}
                  {c.text_preview && (
                    <div className="text-[8px] opacity-70 line-clamp-2 leading-tight mt-0.5 text-[#4E4370]">
                      {c.text_preview}
                    </div>
                  )}
                  {c.block_count > 0 && !c.text_preview && c.stickers.length === 0 && !(c.has_doodle || c.has_image) && (
                    <div className="text-[9px] opacity-50 mt-0.5">{c.block_count}</div>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-[10px] text-[#8B7BAA] flex gap-3 flex-wrap">
        <span>◆ idag</span><span>🌸 klistermärke</span><span>🔔 alarm</span>
      </div>

      {/* Right vertical JAN-DEC rail — hoppar direkt till annan månad */}
      <MonthRail
        year={year}
        activeMonth={month}
        onPickMonth={(y, m) => { setYear(y); setMonth(m); }}
      />
    </div>
  );
}

// ============== INDEX / ÖVERSIKT ==============
// ============== MONTH RAIL (right-side JAN-DEC tabs, Amethyst-style) ==============
const AMETHYST_TABS = [
  { m: 1,  label: 'JAN', bg: '#A78BC7' }, { m: 2,  label: 'FEB', bg: '#A78BC7' },
  { m: 3,  label: 'MAR', bg: '#B5A0D0' }, { m: 4,  label: 'APR', bg: '#B5A0D0' },
  { m: 5,  label: 'MAY', bg: '#C8B6FF' }, { m: 6,  label: 'JUN', bg: '#C8B6FF' },
  { m: 7,  label: 'JUL', bg: '#D7C6E7' }, { m: 8,  label: 'AUG', bg: '#D7C6E7' },
  { m: 9,  label: 'SEP', bg: '#E9D3E8' }, { m: 10, label: 'OCT', bg: '#F0D5E3' },
  { m: 11, label: 'NOV', bg: '#EBC7D3' }, { m: 12, label: 'DEC', bg: '#E2B8C5' },
];

function MonthRail({ year, activeMonth, onPickMonth }) {
  return (
    <div className="hidden md:flex fixed right-0 top-24 z-10 flex-col" data-testid="mysida-month-rail">
      {AMETHYST_TABS.map((t) => {
        const isActive = t.m === activeMonth;
        return (
          <button
            key={t.m}
            onClick={() => onPickMonth(year, t.m)}
            className={`relative w-9 h-11 border-2 border-black border-r-0 rounded-l-md flex items-center justify-center font-black text-white text-[10px] tracking-wider transition-transform ${isActive ? 'scale-110 z-20 shadow-lg' : 'hover:translate-x-[-3px]'}`}
            style={{ background: t.bg, writingMode: 'vertical-rl', textOrientation: 'mixed', marginBottom: '-2px' }}
            data-testid={`month-rail-${t.m}`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ============== INDEX / ÖVERSIKT (Amethyst-style) ==============
// Extracted to ./MinSidaIndexView.js — MinSida keeps just a wrapper that injects MonthRail.
function IndexView({ onSwitchView, onPickDay, onPickListPage }) {
  return (
    <MinSidaIndexView
      onSwitchView={onSwitchView}
      onPickDay={onPickDay}
      onPickListPage={onPickListPage}
      MonthRail={MonthRail}
    />
  );
}

// ============== VIEW TABS ==============
function ViewTabs({ view, onChange }) {
  const tabs = [
    { id: 'index', label: 'Översikt', emoji: '📖', color: 'bg-[#F5EEFF]' },
    { id: 'year',  label: 'År',       emoji: '📆', color: 'bg-[#FDCFE8]' },
    { id: 'month', label: 'Månad',    emoji: '🗓️', color: 'bg-[#C8B6FF]' },
    { id: 'week',  label: 'Vecka',    emoji: '📅', color: 'bg-[#A2D2FF]' },
    { id: 'day',   label: 'Idag',     emoji: '💜', color: 'bg-[#DED0F0]' },
    { id: 'freeform', label: 'Fri sida', emoji: '🌸', color: 'bg-[#FFC8DD]' },
  ];
  return (
    <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="mysida-view-tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1.5 transition ${
            view === t.id ? `${t.color} scale-[1.03]` : 'bg-white opacity-75 hover:opacity-100'
          }`}
          data-testid={`view-tab-${t.id}`}
        >
          <span>{t.emoji}</span>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ============== MAIN ==============
export default function MinSida() {
  const [day, setDay] = useState(todayYmd());
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alarmModalBlock, setAlarmModalBlock] = useState(null);
  const [pendingAlarm, setPendingAlarm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mysida/page`, { params: { day }, withCredentials: true });
      setBlocks(r.data.blocks || []);
    } catch (e) {
      toast.error('Kunde inte ladda sidan');
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  // Poll pending alarms every 30s
  const pollAlarms = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mysida/alarms/pending`, { withCredentials: true });
      const first = (r.data.alarms || [])[0];
      if (first && !pendingAlarm) setPendingAlarm(first);
    } catch {}
  }, [pendingAlarm]);

  useEffect(() => {
    pollAlarms();
    const id = setInterval(pollAlarms, 30000);
    return () => clearInterval(id);
  }, [pollAlarms]);

  const ackAlarm = async (alarmId) => {
    try {
      await axios.post(`${API}/mysida/alarms/ack/${alarmId}`, {}, { withCredentials: true });
    } catch {}
    setPendingAlarm(null);
    // check for next queued
    pollAlarms();
  };

  const addBlock = async (payload) => {
    try {
      const r = await axios.post(`${API}/mysida/page/${day}/block`, payload, { withCredentials: true });
      setBlocks((prev) => [...prev, r.data]);
    } catch {
      toast.error('Kunde inte lägga till blocket');
    }
  };

  const updateBlock = async (blockId, patch) => {
    try {
      const r = await axios.put(`${API}/mysida/page/${day}/block/${blockId}`, patch, { withCredentials: true });
      setBlocks((prev) => prev.map((b) => (b.id === blockId ? r.data : b)));
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const deleteBlock = async (blockId) => {
    try {
      await axios.delete(`${API}/mysida/page/${day}/block/${blockId}`, { withCredentials: true });
      setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const copyFromYesterday = async () => {
    const src = shiftDay(day, -1);
    if (!window.confirm(`Kopiera alla block från igår (${humanDay(src)})? Alarm nollställs.`)) return;
    try {
      const r = await axios.post(`${API}/mysida/page/${day}/copy-from`, { source_day: src }, { withCredentials: true });
      toast.success(`Kopierade ${r.data.copied} block ✨`);
      load();
    } catch {
      toast.error('Inget att kopiera från igår');
    }
  };

  const isToday = day === todayYmd();
  const [view, setView] = useState('index');
  const [listTitle, setListTitle] = useState('');

  const handleSwitchView = useCallback((v, jumpTo) => {
    if (jumpTo) setDay(jumpTo);
    setView(v);
  }, []);

  const pickDay = useCallback((d) => {
    setDay(d);
    setView('day');
  }, []);

  const pickListPage = useCallback((slug, title) => {
    setDay(slug);
    setListTitle(title);
    setView('list');
  }, []);

  return (
    <div className="max-w-5xl mx-auto" data-testid="mysida">
      {/* Alarm popup overlay */}
      {pendingAlarm && <AlarmPopup alarm={pendingAlarm} onAck={ackAlarm} />}

      {/* View switcher */}
      <ViewTabs view={view} onChange={(v) => { if (view === 'list') setDay(todayYmd()); setView(v); }} />

      {view === 'index' && (
        <IndexView onSwitchView={handleSwitchView} onPickDay={pickDay} onPickListPage={pickListPage} />
      )}

      {view === 'list' && (
        <>
          <div className="neo-card p-4 mb-4 bg-gradient-to-br from-[#F5EEFF] via-[#DED0F0] to-[#C8B6FF] flex items-center justify-between gap-3">
            <button onClick={() => setView('index')} className="neo-btn p-2 bg-white" data-testid="list-back-btn">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 text-center">
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-700">Blank sida</div>
              <div className="text-xl sm:text-2xl font-['Fredoka'] font-bold leading-tight text-[#4E4370]">{listTitle}</div>
            </div>
            <div className="w-9" />
          </div>
          <AddBlockPanel onAdd={addBlock} />
          {blocks.length === 0 ? (
            <div className="text-center py-14 opacity-60" data-testid="mysida-empty">
              <div className="text-5xl mb-2">✍️</div>
              <div className="text-sm">Tomt just nu — börja med ett block ovan</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mysida-canvas">
              {blocks.map((b) => (
                <BlockCard
                  key={b.id}
                  block={b}
                  onUpdate={(patch) => updateBlock(b.id, patch)}
                  onDelete={() => deleteBlock(b.id)}
                  onOpenAlarms={setAlarmModalBlock}
                />
              ))}
            </div>
          )}
        </>
      )}

      {view === 'year' && (
        <YearView
          onPickMonth={(y, m) => { setDay(`${y}-${String(m).padStart(2, '0')}-01`); setView('month'); }}
          onPickDay={pickDay}
        />
      )}

      {view === 'month' && (() => {
        const dObj = new Date(`${day}T12:00:00`);
        const y = dObj.getFullYear();
        const m = dObj.getMonth() + 1;
        return (
          <div className="md:pr-12 relative">
            <AmethystMonthlyPlanner
              year={y}
              month={m}
              onChangeMonth={(ny, nm) => setDay(`${ny}-${String(nm).padStart(2,'0')}-01`)}
              onPickDay={pickDay}
            />
            <MonthRail
              year={y}
              activeMonth={m}
              onPickMonth={(ny, nm) => setDay(`${ny}-${String(nm).padStart(2,'0')}-01`)}
            />
          </div>
        );
      })()}

      {view === 'month-old' && (
        <MonthView initialDay={day} onPickDay={pickDay} />
      )}

      {view === 'week' && (
        <div className="md:pr-12 relative" data-testid="mysida-week">
          <AmethystWeeklyPlan day={day} setDay={setDay} />
          <MonthRail
            year={new Date(`${day}T12:00:00`).getFullYear()}
            activeMonth={new Date(`${day}T12:00:00`).getMonth() + 1}
            onPickMonth={(y, m) => { setDay(`${y}-${String(m).padStart(2, '0')}-01`); setView('month'); }}
          />
        </div>
      )}

      {view === 'day' && (
        <div className="md:pr-12 relative">
          <AmethystDailyPlanner day={day} setDay={setDay} />
          <MonthRail
            year={new Date(`${day}T12:00:00`).getFullYear()}
            activeMonth={new Date(`${day}T12:00:00`).getMonth() + 1}
            onPickMonth={(y, m) => { setDay(`${y}-${String(m).padStart(2, '0')}-01`); setView('month'); }}
          />
        </div>
      )}

      {view === 'freeform' && (
        <>
          {/* Amethyst Daily Planner header */}
          <div className="neo-card p-4 mb-4 bg-gradient-to-br from-[#F5EEFF] via-[#DED0F0] to-[#C8B6FF] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setDay(shiftDay(day, -1))} className="neo-btn p-2 bg-white" data-testid="prev-day-btn">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-700">Fri sida</div>
            <div className="text-xl font-['Fredoka'] font-bold leading-tight capitalize">{humanDay(day)}</div>
            {!isToday && (
              <button onClick={() => setDay(todayYmd())} className="text-xs underline mt-0.5" data-testid="jump-today-btn">
                → gå till idag
              </button>
            )}
          </div>
          <button onClick={() => setDay(shiftDay(day, 1))} className="neo-btn p-2 bg-white" data-testid="next-day-btn">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <button onClick={copyFromYesterday} className="neo-btn px-3 py-2 bg-white text-sm font-bold flex items-center gap-1.5" data-testid="copy-yesterday-btn">
          <Copy className="w-4 h-4" /> Kopiera från igår
        </button>
      </div>

      {/* Week strip: 7-day thumbnails */}
      <WeekStrip day={day} onPick={setDay} refreshKey={blocks.length} />

      {/* Add toolbar */}
      <AddBlockPanel onAdd={addBlock} />

      {/* Canvas */}
      {loading ? (
        <div className="text-center opacity-60 py-10">Laddar sidan… 🌸</div>
      ) : blocks.length === 0 ? (
        <div className="neo-card p-10 text-center bg-white/60 border-dashed" data-testid="mysida-empty">
          <div className="text-4xl mb-2">🌸</div>
          <div className="text-lg font-['Fredoka'] font-bold">En helt tom sida</div>
          <div className="text-sm opacity-70 mt-1">
            Använd verktygsraden för att lägga till text, bockar, klistermärken eller bilder.
            Klicka på 🔔 på ett block för att lägga upp till 3 alarm.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mysida-canvas">
          {blocks.map((b) => (
            <BlockCard
              key={b.id}
              block={b}
              onUpdate={(patch) => updateBlock(b.id, patch)}
              onDelete={() => deleteBlock(b.id)}
              onOpenAlarms={setAlarmModalBlock}
            />
          ))}
        </div>
      )}
        </>
      )}

      {/* Alarm modal */}
      {alarmModalBlock && (
        <AlarmModal
          block={alarmModalBlock}
          day={day}
          onClose={() => setAlarmModalBlock(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
